/* Reusable Banner-Component for "Template / No Budget" fallback state.
 *
 * Rendered whenever a backend generative endpoint returns
 *   { source: "template_fallback", fallback_reason: "..." }
 * — the specific ``no_active_budget_frame`` reason gets an actionable
 * CTA that deep-links to the Budget page so the tenant can set their
 * frame without hunting through the settings tree.
 *
 * All other fallback reasons render a neutral banner with the raw
 * reason as diagnostic tail (unchanged behaviour vs. the previous
 * inline badge).
 *
 * German UI copy; English code. */
import { Link } from "react-router-dom";
import { AlertTriangle } from "lucide-react";
import { Button } from "../ui/button";

const REASON_LABELS = {
  no_active_budget_frame:
    "Basisvorlage aktiv — Budget-Rahmen fehlt.",
  budget_exceeded:
    "Basisvorlage aktiv — Budget-Rahmen erschöpft.",
  agent_error:
    "KI-Vorschlag momentan nicht verfügbar — Basisvorlage angezeigt.",
  regeneration_cap_reached:
    "Neu-Generierungs-Limit erreicht — Basisvorlage angezeigt.",
};

export function TemplateFallbackBanner({ reason, className = "", testId }) {
  if (!reason) return null;
  const label = REASON_LABELS[reason]
    || "KI-Vorschlag nicht verfügbar — Basisvorlage angezeigt.";
  const isBudget = reason === "no_active_budget_frame"
    || reason === "budget_exceeded";

  return (
    <div
      data-testid={testId || "template-fallback-banner"}
      data-reason={reason}
      className={`text-xs rounded-md border border-amber-400/50 bg-amber-50 dark:bg-amber-950/30 text-amber-900 dark:text-amber-100 px-3 py-2 flex items-start gap-2 ${className}`}
    >
      <AlertTriangle className="size-3.5 mt-0.5 shrink-0" aria-hidden />
      <div className="flex-1 space-y-1">
        <div data-testid="template-fallback-banner-label">
          {label}
        </div>
        <div className="opacity-70" data-testid="template-fallback-banner-reason">
          Grund: <code>{reason}</code>
        </div>
        {isBudget && (
          <div className="pt-1">
            <Button
              asChild
              size="sm"
              variant="outline"
              className="h-6 px-2 text-xs"
              data-testid="template-fallback-banner-cta"
            >
              <Link to="/app/settings/budget">
                Budget einrichten →
              </Link>
            </Button>
          </div>
        )}
      </div>
    </div>
  );
}

export default TemplateFallbackBanner;
